import React from "react";
import { Box, Chip, Typography } from "@mui/material";

interface ComponentProps {
  symbol: string;
}

const usdtLocale = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    maximumSignificantDigits: 3,
  });

// The bulk mcap feed is ~275KB, so fetch it once and share across all instances
let tagsFeedPromise: Promise<Map<string, string[]>> | null = null;

function fetchTagsByCoin(): Promise<Map<string, string[]>> {
    if (!tagsFeedPromise) {
        tagsFeedPromise = fetch("https://news.kryptrader.com/mcap", {
            method: "GET",
        })
            .then((response) => response.json())
            .then((data) => {
                let tagsByCoin = new Map<string, string[]>();
                let capByCoin = new Map<string, number>();
                data.forEach((s: any) => {
                    let coin = s.symbol.toUpperCase();
                    // Duplicate symbols exist in the feed; keep the entry with the biggest cap
                    if (!capByCoin.has(coin) || s.market_cap > (capByCoin.get(coin) || 0)) {
                        capByCoin.set(coin, s.market_cap);
                        tagsByCoin.set(coin, s.tags || []);
                    }
                });
                return tagsByCoin;
            })
            .catch(() => {
                tagsFeedPromise = null;
                return new Map<string, string[]>();
            });
    }
    return tagsFeedPromise;
}

function fetchTags(symbol: string): Promise<string[]> {
    return fetch(`https://news.kryptrader.com/tags/${symbol}`, {
        method: "GET",
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error("tags endpoint unavailable");
            }
            return response.json();
        })
        .catch(() =>
            // Fall back to the bulk feed until the /tags endpoint is deployed
            fetchTagsByCoin().then((tagsByCoin) => tagsByCoin.get(symbol.toUpperCase()) || [])
        );
}

function SymbolMarketCap(props: ComponentProps) {

  const [mcap, setMcap] = React.useState<number>(0);
  const [tags, setTags] = React.useState<string[]>([]);

    React.useEffect(() => {
        fetch(`https://news.kryptrader.com/mcap/${props.symbol}`, {
            method: "GET",
        })
            .then((response) => response.text())
            .then((data) => {
                //let matches = data.filter((d:any) => d.symbol.toUpperCase() === props.symbol);
                try {
                    if (data) {
                        //matches = matches.sort((a:any, b:any) => a.market_cap < b.market_cap ? 1 : -1);
                        setMcap(parseInt(data));
                    }
                }
                catch {}
        });
    }, [])

    React.useEffect(() => {
        let cancelled = false;
        fetchTags(props.symbol).then((coinTags) => {
            if (!cancelled) {
                setTags(coinTags);
            }
        });
        return () => { cancelled = true; };
    }, [props.symbol])

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <Typography color="text.secondary" variant="body2" component="span" sx={{ ml: 0 }}>
            {
            usdtLocale.format(mcap)
            }
        </Typography>
        {tags.map((tag) => (
            <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem", borderRadius: 0 }}
            />
        ))}
    </Box>
  );
}

export default SymbolMarketCap;
